import { LazyLoadImage, LazyLoadImageProps } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';

const StyledImage = styled(LazyLoadImage)<LazyLoadImageProps>`
  width: ${props => (props.visibleByDefault && props.width ? props.width : '100%')};
  height: ${props => (props.visibleByDefault && props.height ? props.height : '100%')};
  img {
    max-width: 100%;
  }
`;

type PlaceholderWrapperProps = {
  height?: string;
  width?: string;
  alt?: string;
};

const StyledPlaceholderWrapper = styled.div<PlaceholderWrapperProps>`
  > * {
    width: 100% !important;
    height: 100% !important;
  }
  ${props =>
    props.height &&
    css`
      height: ${props.height};
    `}
  ${props =>
    props.width &&
    css`
      width: ${props.width};
      display: inline-block;
    `}
`;

type ImageProps = {
  alt?: string;
  height?: string;
  width?: string;
  src: string;
  className?: string;
  placeholderSrc?: string;
  placeholder?: JSX.Element;
  displayWithoutEffect?: boolean;
  id?: string;
};

type LocalPlaceholderProps = {
  className?: string;
};

export const Image = ({
  src,
  alt = ' ',
  height,
  width,
  className,
  placeholderSrc,
  placeholder,
  displayWithoutEffect,
  id
}: ImageProps) => {
  const [source, setSource] = useState(src);
  const [showPlaceholder, setShowPlaceholder] = useState(false);

  useEffect(() => {
    if (source !== src) {
      setSource(src);
    }
  }, [src]);

  const handleImageErrored = () => {
    if (placeholderSrc) {
      setSource(placeholderSrc);
    } else if (!placeholderSrc && placeholder) {
      setShowPlaceholder(true);
    }
  };

  const LocalPlaceholder = <PROPS extends LocalPlaceholderProps>({
    ...rest
  }: PROPS): JSX.Element => <div {...rest} />;

  return !showPlaceholder ? (
    <StyledImage
      id={id}
      onError={handleImageErrored}
      className={className}
      alt={alt}
      height={height}
      src={source || ''}
      width={width}
      effect="blur"
      placeholderSrc={placeholderSrc}
      placeholder={placeholder}
      visibleByDefault={displayWithoutEffect}
      wrapperClassName={`imageWrapper wrapper_${className}`}
    />
  ) : (
    <StyledPlaceholderWrapper className={className} height={height} width={width} alt={alt}>
      <LocalPlaceholder className="localPlaceholder" />
    </StyledPlaceholderWrapper>
  );
};

export default Image;
